import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef, ViewChildren } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Employment } from 'src/app/shared/models/employment.model';
import { environment } from 'src/environments/environment.dev';
import { ROUTE_DQF, ROUTE_EMPLOYED_FORM, ROUTE_EMPLOYMENT_HISTORY, ROUTE_UNEMPLOYED_FORM } from 'src/app/shared/routes';
import { Driver } from 'src/app/shared/models/driver.model';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { SkeletonEmpResidenceHistoryComponent } from '../skeleton-emp-residence-history/skeleton-emp-residence-history.component';
import { EmploymentViewComponent } from '../employment-view/employment-view.component';
import { compareEmploymentByToDateDesc } from 'src/app/shared/utils/employment-sort.util';
import { isEmployedRecordMissingMandatoryFields } from 'src/app/shared/utils/employment-completeness.util';
import dayjs from 'dayjs';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY'
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
    selector: 'app-employment-history',
    templateUrl: './employment-history.component.html',
    styleUrls: ['./employment-history.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        DatePipe
    ],
    standalone: true,
    imports: [
      CommonModule,
      MatButtonModule,
      MatIconModule,
      MatMenuModule,
      MatProgressBarModule,
      SkeletonEmpResidenceHistoryComponent,
      EmploymentViewComponent
    ]
})
export class EmploymentHistoryComponent implements OnInit, OnDestroy {
  employmentList = [];
  form: UntypedFormGroup;
  errorMessage: string;
  progressBarValue: number = 100;
  currentDate = new Date();
  currentMonthYear: string;
  lastDate: Date;
  lastMonthYear: string;
  isGreaterThan10years: boolean = false;
  defaultToDate: Date;
  minFromDate;
  maxToDate: Date;
  isContentReady: boolean = false;
  driver: Driver;
  showAddNewCurrentRecordText: boolean;
  showStaleEmployerBanner: boolean = false;
  currentEmployerName: string = '';

  @ViewChild('experienceForm', { read: ViewContainerRef }) experienceTemplateForm: ViewContainerRef;
  @ViewChild('unemploymentForm', { read: ViewContainerRef }) unemploymentTemplateForm: ViewContainerRef;
  @ViewChildren('toDatePicker') toDateList;
  @ViewChildren('fromDatePicker') fromDateList;
  @ViewChildren('unEmpFromDatePicker') unEmpFromDateList;
  @ViewChildren('unEmpToDatePicker') unEmpToDateList;
  private subs = new SubSink();

  constructor(
    private readonly router: Router,
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly toastService: ToastService,
    private readonly storageService: StorageService,
    private readonly datePipe: DatePipe
  ) {
    this.errorMessage = undefined;
    this.initializeDates();
  }

  private initializeDates() {
    // Get current date and set to end of month
    this.currentDate = new Date();
    this.currentDate.setMonth(this.currentDate.getMonth() + 1, 0); // End of current month
    this.currentMonthYear = this.datePipe.transform(this.currentDate, 'MMMM yyyy') || '';
    
    // Calculate date 10 years ago
    this.lastDate = new Date(this.currentDate);
    this.lastDate.setFullYear(this.lastDate.getFullYear() - 10);
    this.lastMonthYear = this.datePipe.transform(this.lastDate, 'MMMM yyyy') || '';
    
    this.maxToDate = new Date(this.currentDate);
  }

  private formatDateToYearMonth(date: Date): string {
    const d = new Date(date);
    d.setDate(1); // Set to first day of month for comparison
    return this.datePipe.transform(d, 'yyyy MM') || '';
  }

  ngOnInit(): void {
    this.loadExperience();
  }

  openRequirementsActionSheet() {
    const currentAddress: Employment = this.employmentList.find(emp => emp.isCurrentEmployment == true);
    if (!currentAddress) {
      return;
    }
    // Show inline banner instead of bottom sheet
    this.currentEmployerName = currentAddress.employer || 'your current employer';
    this.showStaleEmployerBanner = true;
  }

  onStaleConfirmYes(): void {
    const currentAddress: Employment = this.employmentList.find(emp => emp.isCurrentEmployment == true);
    if (!currentAddress) { return; }
    this.showStaleEmployerBanner = false;
    this.isContentReady = false;
    const addressModel: any = { ...currentAddress };
    const id = addressModel.id;
    delete addressModel.id;
    delete addressModel.modifiedDate;
    delete addressModel.creationDate;
    delete addressModel.version;
    this.registrationService.updateEmployment(addressModel, id).subscribe(
      () => { this.errorMessage = undefined; this.loadExperience(); },
      (error: any) => { this.toastService.showError(error?.message); this.isContentReady = true; }
    );
  }

  onStaleConfirmNo(): void {
    const currentAddress: Employment = this.employmentList.find(emp => emp.isCurrentEmployment == true);
    if (!currentAddress) { return; }
    this.showStaleEmployerBanner = false;
    // Navigate to edit form so driver can set their end date
    this.router.navigateByUrl(`/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_EMPLOYED_FORM}/${currentAddress.id}`);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onAddNewEmployment(isMissingIndex: number): void {
    let path = `/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_EMPLOYED_FORM}`;
    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const prevEmp = this.employmentList[isMissingIndex - 1];
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const q = new URLSearchParams();
      q.set('missingIndex', String(isMissingIndex));
      if (prevEmp?.fromDate) q.set('gapPrevFromDate', dayjs(prevEmp.fromDate).format('YYYY-MM-DD'));
      if (nextEmp?.toDate) q.set('gapNextToDate', dayjs(nextEmp.toDate).format('YYYY-MM-DD'));
      path += `?${q.toString()}`;
    }
    this.router.navigateByUrl(path);
  }

  onAddNewCurrentEmployment(isEmploymentForm: boolean): void {
    this.storageService.store('currentEmploymentState', null);
    let path = '';
    if (isEmploymentForm) {
      path = `/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_EMPLOYED_FORM}?newCurrentAddress=true`;
    } else {
      path = `/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_UNEMPLOYED_FORM}?newCurrentAddress=true`;
    }
    this.router.navigateByUrl(path);
  }

  onAddUnemployment(isMissingIndex: number): void {
    let path = `/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_UNEMPLOYED_FORM}`;
    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const prevEmp = this.employmentList[isMissingIndex - 1];
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const q = new URLSearchParams();
      q.set('missingIndex', String(isMissingIndex));
      if (prevEmp?.fromDate) q.set('gapPrevFromDate', String(prevEmp.fromDate));
      if (nextEmp?.toDate) q.set('gapNextToDate', String(nextEmp.toDate));
      path += `?${q.toString()}`;
    }
    this.router.navigateByUrl(path);
  }

  onDeleteEmploymentPressed(employment: Employment): void {
    this.isContentReady = false;
    this.subs.add(
      this.registrationService.deleteEmployment(employment).subscribe(
        () => {
          this.errorMessage = undefined;
          let newRecords = this.employmentList.filter(emp => emp.id !== employment.id);

          // If we deleted the current employment, make the most recent one current
          if (employment.isCurrentEmployment && newRecords.length > 0) {
            newRecords.sort((a, b) => compareEmploymentByToDateDesc(a, b, this.currentDate));

            // Set the first record as current if it's valid
            if (newRecords[0] && newRecords[0].fromDate !== null) {
              newRecords[0].isCurrentEmployment = true;
              // If it was current, it shouldn't have toDate
              delete newRecords[0].toDate;
            }
          }
          this.registrationService.updateEmploymentHistoryStore(newRecords);
          this.loadExperience();
        },
        (error: string) => {
          this.toastService.showError(error || environment.errorMessage);
          this.isContentReady = true;
        }
      )
    );
  }

  onUpdateEmploymentPressed($event): void {
    if ($event.employmentState == 'unemployed') {
      this.router.navigateByUrl(`/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_UNEMPLOYED_FORM}/${$event.id}`);
    } else {
      this.router.navigateByUrl(`/${ROUTE_EMPLOYMENT_HISTORY}/${ROUTE_EMPLOYED_FORM}/${$event.id}`);
    }
  }

  goBack(): void {
    this.location.back();
  }

  get canShowAddButton(): boolean {
    return true;
  }

  isOnlyCurrentEmployment(employmentList: Employment[]): boolean {
    if (!employmentList || employmentList.length !== 1) return false;

    const onlyEmployment = employmentList[0];
    return !!onlyEmployment?.isCurrentEmployment;
  }

  employmentRecordNeedsAttention(employment: Employment): boolean {
    return isEmployedRecordMissingMandatoryFields(employment);
  }

  private loadExperience(): void {
    this.isContentReady = false;
    this.showAddNewCurrentRecordText = false;
    this.subs.add(
      this.registrationService.getEmploymentHistory().subscribe(res => {
        this.employmentList = [...res];

        this.validateEmploymentData();

        const currentEmployment = this.employmentList.find(e => e.isCurrentEmployment);
        
        if (this.employmentList?.length) {
          this.employmentList.sort((a, b) => compareEmploymentByToDateDesc(a, b, this.currentDate));

          this.checkMissingGap();
          
          // if there is no currentEmployment in employmentlist
          if (this.employmentList.length > 0 && !currentEmployment) {
            this.progressBarValue = 90;
          }
        } else {
          this.isGreaterThan10years = false;
          this.progressBarValue = 100;
          this.defaultToDate = this.currentDate;
        }
        
        this.registrationService.loadDriver().subscribe(() => {
          this.driver = this.registrationService.getDriver();
          if (this.location.path().includes(ROUTE_DQF)) {
            if (
              !this.driver.employmentRequirementsCompleted &&
              currentEmployment &&
              currentEmployment.modifiedDate &&
              this.formatDateToYearMonth(new Date(currentEmployment.modifiedDate)) <
                this.formatDateToYearMonth(this.currentDate)
            ) {
              this.openRequirementsActionSheet();
              this.progressBarValue = 90;
            } else {
              // after requirements selection stage user refreshed the page
              if (!this.driver.employmentRequirementsCompleted && this.employmentList.length && !currentEmployment) {
                this.showAddNewCurrentRecordText = true;
              }
            }
          }
          this.isContentReady = true;
        });
      })
    );
  }
  private validateEmploymentData(): void {
    if (!this.employmentList || this.employmentList.length === 0) {
      return;
    }

    // Work on a cloned copy to avoid modifying frozen objects
    const mutableList = this.employmentList.map(emp => ({ ...emp }));

    let currentCount = 0;
    let currentIndices = [];

    mutableList.forEach((emp, index) => {
      if (emp && emp.isCurrentEmployment) {
        currentCount++;
        currentIndices.push(index);
      }
    });

    if (currentCount > 1) {
      const sortedIndices = [...currentIndices].sort((a, b) =>
        compareEmploymentByToDateDesc(mutableList[a], mutableList[b], this.currentDate)
      );

      for (let i = 1; i < sortedIndices.length; i++) {
        const index = sortedIndices[i];
        mutableList[index].isCurrentEmployment = false;

        if (!mutableList[index].toDate) {
          const fromDate = new Date(mutableList[sortedIndices[0]].fromDate);
          fromDate.setMonth(fromDate.getMonth() - 1);
          mutableList[index].toDate = this.datePipe.transform(fromDate, 'yyyy-MM-dd');
        }
      }

      this.registrationService.updateEmploymentHistoryStore(mutableList);
    }

    if (currentCount === 0 && mutableList.length > 0) {
      const sortedEmployments = [...mutableList]
        .filter(emp => emp.fromDate !== null)
        .sort((a, b) => compareEmploymentByToDateDesc(a, b, this.currentDate));

      if (sortedEmployments.length > 0) {
        const mostRecent = sortedEmployments[0];
        const index = mutableList.findIndex(emp => emp.id === mostRecent.id);

        if (index !== -1) {
          mutableList[index].isCurrentEmployment = true;
          delete mutableList[index].toDate;

          this.registrationService.updateEmploymentHistoryStore(mutableList);
        }
      }
    }
  }


  private checkMissingGap(): void {
    const employmentData: any[] = [...this.employmentList];

    if (this.employmentList.length > 1) {
      let spliceIndex = 0;
      for (let index = 0; index < employmentData.length - 1; index++) {
        if (employmentData[index + 1]) {
          const date2CompareToDate = new Date(employmentData[index + 1].toDate);
          const date2CompareFromDate = new Date(employmentData[index].fromDate);
          const monthDiff = Math.round(
            (date2CompareToDate.getTime() - date2CompareFromDate.getTime()) /
              (1000 * 60 * 60 * 24 * 30.44)
          );
          const periodsOverlap = this.employmentPeriodsOverlap(employmentData[index], employmentData[index + 1]);
          if (monthDiff === 0 || periodsOverlap) {
            // Adjacent or overlapping — no gap row
          } else {
            if (!spliceIndex) {
              spliceIndex = index + 1;
            } else {
              spliceIndex += index;
            }
            if (employmentData.length - 1 === index + 1 && employmentData.length - 2 === index) {
              this.employmentList.splice(this.employmentList.length - 1, 0, { fromDate: null } as Employment);
            } else {
              this.employmentList.splice(spliceIndex, 0, { fromDate: null } as Employment);
            }
          }
        }
      }
    }

    this.applyMergedCoverageProgress();
  }

  private applyMergedCoverageProgress(): void {
    const mergedMonths = this.getUnionCoverageMonthsWithinWindow();
    this.isGreaterThan10years = this.hasContinuousFullWindowCoverage();
    this.updateProgressBarFromMergedMonths(mergedMonths);
  }

  private getWindowBoundsMs(): { windowStart: number; windowEnd: number } {
    const windowStart = dayjs(this.lastDate).startOf('month').valueOf();
    const windowEnd = dayjs(this.currentDate).endOf('day').valueOf();
    return { windowStart, windowEnd };
  }

  private collectIntervalsClampedToWindow(): { start: number; end: number }[] {
    const { windowStart, windowEnd } = this.getWindowBoundsMs();
    const intervals: { start: number; end: number }[] = [];

    for (const emp of this.employmentList) {
      if (!emp?.fromDate) {
        continue;
      }
      const fromMs = dayjs(emp.fromDate).startOf('day').valueOf();
      const toMs = emp.toDate
        ? dayjs(emp.toDate).endOf('day').valueOf()
        : dayjs(this.currentDate).endOf('day').valueOf();
      const lo = Math.max(fromMs, windowStart);
      const hi = Math.min(toMs, windowEnd);
      if (lo <= hi) {
        intervals.push({ start: lo, end: hi });
      }
    }

    return intervals;
  }

  private mergeEmploymentIntervals(intervals: { start: number; end: number }[]): { start: number; end: number }[] {
    if (!intervals.length) {
      return [];
    }
    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];
    let cur = { ...sorted[0] };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      if (next.start <= cur.end + 86400000) {
        cur.end = Math.max(cur.end, next.end);
      } else {
        merged.push(cur);
        cur = { ...next };
      }
    }
    merged.push(cur);
    return merged;
  }

  private getUnionCoverageMonthsWithinWindow(): number {
    const merged = this.mergeEmploymentIntervals(this.collectIntervalsClampedToWindow());
    const MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30.44;
    let totalMs = 0;
    for (const m of merged) {
      totalMs += m.end - m.start;
    }
    return totalMs / MS_PER_MONTH;
  }

  private hasContinuousFullWindowCoverage(): boolean {
    const { windowStart, windowEnd } = this.getWindowBoundsMs();
    const merged = this.mergeEmploymentIntervals(this.collectIntervalsClampedToWindow());
    if (merged.length !== 1) {
      return false;
    }
    const { start, end } = merged[0];
    return start <= windowStart && end >= windowEnd;
  }

  private updateProgressBarFromMergedMonths(mergedMonths: number): void {
    const coveredPercent = Math.min(100, (mergedMonths / 120) * 100);
    this.progressBarValue = coveredPercent >= 100 ? 0 : 100 - coveredPercent;
  }

  private employmentPeriodsOverlap(a: Employment, b: Employment): boolean {
    if (!a?.fromDate || !b?.fromDate) {
      return false;
    }
    const aStart = new Date(a.fromDate).getTime();
    const bStart = new Date(b.fromDate).getTime();
    const aEnd = a.toDate ? new Date(a.toDate).getTime() : new Date(this.currentDate).getTime();
    const bEnd = b.toDate ? new Date(b.toDate).getTime() : new Date(this.currentDate).getTime();
    return aStart <= bEnd && bStart <= aEnd;
  }
}
